import { CapacitorHttp } from "@capacitor/core";
import { isNative, platform } from "./capacitor";

/**
 * Native HTTP for the iOS shell (#379).
 *
 * The iOS WebView serves the app from a custom-scheme origin
 * (`pb-ranks://localhost`, `ios.scheme` in capacitor.config.ts) and API
 * Gateway refuses non-http(s) origins in its CORS allow-list ("Invalid format
 * for origin", #290), so a plain fetch from the iOS app dies at the preflight
 * before the Lambda ever runs. Android serves from `https://localhost`, which
 * IS allow-listed, and keeps using fetch.
 *
 * Only the API client and the S3 upload call this, explicitly. The global
 * `CapacitorHttp.enabled` patch was deliberately not used: it would also
 * route mapbox tiles, fonts and every other request through native code.
 * Native requests ignore AbortSignal and report no upload progress.
 */

export const usesNativeHttp = (): boolean => isNative() && platform() === "ios";

export type NativeInit = {
  method?: string;
  headers?: Record<string, string>;
  /** Already-serialised body (the API client always sends JSON text). */
  body?: string;
  /** Follow 3xx natively. Off by default: a redirect would carry the request headers along. */
  followRedirects?: boolean;
};

/** A fetch-shaped call through CapacitorHttp that returns a real `Response`. */
export async function nativeFetch(url: string, init: NativeInit = {}): Promise<Response> {
  const res = await CapacitorHttp.request({
    // Absolute: a live-reload build talks to the dev server's relative /api proxy.
    url: new URL(url, window.location.href).toString(),
    method: init.method ?? "GET",
    headers: init.headers,
    data: init.body,
    disableRedirects: !init.followRedirects,
    responseType: "text",
  });
  const body = typeof res.data === "string" ? res.data : res.data == null ? "" : JSON.stringify(res.data);
  const headers = new Headers();
  for (const [k, v] of Object.entries(res.headers ?? {})) if (v != null) headers.set(k, String(v));
  // Response() refuses a body for 204/205/304, and any status outside 200-599.
  const status = res.status >= 200 && res.status <= 599 ? res.status : 500;
  const nullBody = status === 204 || status === 205 || status === 304;
  return new Response(nullBody ? null : body, { status, headers });
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error ?? new Error("Could not read the file"));
    reader.onload = () => {
      const url = String(reader.result ?? "");
      resolve(url.slice(url.indexOf(",") + 1));
    };
    reader.readAsDataURL(file);
  });
}

/**
 * POST a file to a presigned S3 form (the same fields + `file` the browser
 * would send as multipart) through the native layer. Entry order matters to
 * S3: the policy fields first, `file` last.
 */
export async function nativeFormPost(url: string, fields: Record<string, string>, file: File): Promise<void> {
  const entries = [
    ...Object.entries(fields).map(([key, value]) => ({
      key,
      value,
      type: "string",
    })),
    {
      key: "file",
      value: await fileToBase64(file),
      type: "base64File",
      fileName: file.name,
      contentType: file.type || "application/octet-stream",
    },
  ];
  const res = await CapacitorHttp.request({
    url,
    method: "POST",
    headers: { "Content-Type": "multipart/form-data" },
    data: entries,
    dataType: "formData",
  });
  if (res.status < 200 || res.status >= 300) throw new Error(`Storage upload failed (${res.status})`);
}
